import type { MissingDocument, RemedialAction } from "@/services/api/case-checking";

const UPLOAD_TITLE_RE = /^Upload\s+/i;
const OS_WINDOW_SCROLL_SELECTOR = '[data-testid="window-content-scroll"]';

export interface ResolvedUploadDocument {
  documentType: string;
  documentName: string;
}

export function isUploadMissingDocumentAction(action: {
  source?: string;
  title: string;
}): boolean {
  return action.source === "completeness" && UPLOAD_TITLE_RE.test(action.title);
}

export function resolveUploadActionDocument(
  action: RemedialAction,
  missingDocuments: MissingDocument[],
): ResolvedUploadDocument | null {
  if (!isUploadMissingDocumentAction(action)) return null;

  const nameFromTitle = action.title.replace(UPLOAD_TITLE_RE, "").trim();
  const byName = missingDocuments.find((m) => m.document_name === nameFromTitle);
  if (byName) {
    return { documentType: byName.document_type, documentName: byName.document_name };
  }

  const byReason = missingDocuments.find((m) => m.reason === action.description);
  if (byReason) {
    return { documentType: byReason.document_type, documentName: byReason.document_name };
  }

  if (!nameFromTitle) return null;
  return { documentType: "", documentName: nameFromTitle };
}

const HIGHLIGHT_CLASSES = [
  "ring-2",
  "ring-indigo-500/60",
  "ring-offset-2",
  "ring-offset-slate-950",
] as const;

export const CHECKLIST_SECTION_UNIVERSAL = "Universal requirements";
export const CHECKLIST_SECTION_CONDITIONAL = "Conditional requirements";

/** Lower values scroll first when the same document type appears in multiple sections. */
export const CHECKLIST_SECTION_PRIORITY = {
  universal: 0,
  caseSpecific: 100,
  subtypeBase: 200,
  conditional: 1000,
} as const;

export interface ChecklistScrollCandidate {
  documentType: string;
  sectionKey: string;
  sectionPriority: number;
  missing: boolean;
}

export function isBetterChecklistScrollCandidate(
  next: ChecklistScrollCandidate,
  current: ChecklistScrollCandidate,
): boolean {
  if (next.missing !== current.missing) return next.missing;
  return next.sectionPriority < current.sectionPriority;
}

/** Pick which checklist section to expand for each document type. */
export function buildChecklistScrollTargetMap(
  candidates: ChecklistScrollCandidate[],
): Map<string, string> {
  const bestByType = new Map<string, ChecklistScrollCandidate>();
  for (const candidate of candidates) {
    const existing = bestByType.get(candidate.documentType);
    if (!existing || isBetterChecklistScrollCandidate(candidate, existing)) {
      bestByType.set(candidate.documentType, candidate);
    }
  }
  return new Map(
    [...bestByType.entries()].map(([documentType, { sectionKey }]) => [documentType, sectionKey]),
  );
}

function parseSectionPriority(el: HTMLElement): number {
  const raw = el.dataset.checklistSectionPriority;
  const parsed = raw ? Number(raw) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : 9999;
}

function isMissingChecklistRow(el: HTMLElement): boolean {
  return el.dataset.checklistMissing === "true";
}

/** Prefer missing required rows; within that, lower section priority (universal first). */
export function pickChecklistScrollRow(documentType: string): HTMLElement | null {
  const rows = Array.from(
    document.querySelectorAll(`[data-checklist-type="${CSS.escape(documentType)}"]`),
  ).filter((node): node is HTMLElement => node instanceof HTMLElement);

  if (rows.length === 0) {
    const legacy = document.querySelector(`[data-testid="checklist-row-${documentType}"]`);
    return legacy instanceof HTMLElement ? legacy : null;
  }

  const missingRows = rows.filter(isMissingChecklistRow);
  const pool = missingRows.length > 0 ? missingRows : rows;
  pool.sort((a, b) => parseSectionPriority(a) - parseSectionPriority(b));
  return pool[0] ?? null;
}

function isVerticalScrollContainer(el: HTMLElement): boolean {
  const { overflowY } = window.getComputedStyle(el);
  return (
    (overflowY === "auto" || overflowY === "scroll") && el.scrollHeight > el.clientHeight + 1
  );
}

export function findScrollContainerForElement(el: HTMLElement): HTMLElement | null {
  const osBoundary = el.closest(OS_WINDOW_SCROLL_SELECTOR);
  let node: HTMLElement | null = el.parentElement;

  while (node) {
    if (isVerticalScrollContainer(node)) return node;
    if (osBoundary && node === osBoundary) {
      return osBoundary instanceof HTMLElement ? osBoundary : null;
    }
    if (!osBoundary && (node === document.body || node === document.documentElement)) break;
    node = node.parentElement;
  }

  return osBoundary instanceof HTMLElement ? osBoundary : null;
}

export function scrollWithinContainer(
  el: HTMLElement,
  container: HTMLElement,
  options?: { offsetTop?: number; behavior?: ScrollBehavior },
): void {
  const offsetTop = options?.offsetTop ?? 12;
  const behavior = options?.behavior ?? "smooth";
  const elRect = el.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const nextTop = elRect.top - containerRect.top + container.scrollTop - offsetTop;
  const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
  const top = Math.min(Math.max(0, nextTop), maxTop);

  container.scrollTo({ top, behavior });
}

export function scrollToChecklistItem(
  documentType: string,
  options?: { highlightMs?: number },
): boolean {
  const el = pickChecklistScrollRow(documentType);
  if (!el) return false;

  const container = findScrollContainerForElement(el);
  if (container) {
    scrollWithinContainer(el, container);
  } else {
    el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  el.classList.add(...HIGHLIGHT_CLASSES);

  const highlightMs = options?.highlightMs ?? 2000;
  window.setTimeout(() => {
    el.classList.remove(...HIGHLIGHT_CLASSES);
  }, highlightMs);

  return true;
}
